import { useEffect, useState } from 'react'
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type { MetricDef, WireStatus } from './types'
import { RECIPE_BINDINGS, recipeFor, recipeMarkdown } from './recipes'

function wireBadge(status: WireStatus) {
  if (status === 'existing') {
    return <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Existing tables</span>
  }
  if (status === 'proxy') {
    return <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Proxy today</span>
  }
  return <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">Needs event</span>
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1600)
      }}
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
    >
      {copied ? (
        <ClipboardDocumentCheckIcon className="h-4 w-4 text-emerald-600" />
      ) : (
        <ClipboardDocumentIcon className="h-4 w-4 text-gray-500" />
      )}
      {copied ? 'Copied' : label}
    </button>
  )
}

export function MetricInfoButton({
  metric,
  onOpen,
  className = '',
}: {
  metric: MetricDef
  onOpen: (m: MetricDef) => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        onOpen(metric)
      }}
      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 shrink-0 ${className}`}
      aria-label={`How to calculate ${metric.name} in the backend`}
      title="How to calculate this in the backend"
    >
      <span className="text-[11px] font-bold leading-none italic">i</span>
    </button>
  )
}

export function MetricInfoDrawer({
  metric,
  onClose,
}: {
  metric: MetricDef
  onClose: () => void
}) {
  const recipe = recipeFor(metric)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" className="flex-1 bg-black/30" aria-label="Close" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto h-full">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start gap-3 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900 leading-snug">{metric.name}</h2>
              {wireBadge(metric.wire)}
            </div>
            <p className="text-xs font-mono text-gray-500 mt-1">
              {`metrics['${metric.id}'].value`}
            </p>
          </div>
          <CopyButton text={recipeMarkdown(metric)} label="Copy all" />
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5 text-sm text-gray-700">
          <p className="text-gray-600">{metric.tellsYou}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Returns</p>
              <p className="font-mono text-xs mt-0.5">{recipe.returns}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Grain</p>
              <p className="text-xs mt-0.5">{metric.grain}</p>
            </div>
            {recipe.numerator && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 sm:col-span-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700">Numerator</p>
                <p className="text-xs mt-0.5">{recipe.numerator}</p>
              </div>
            )}
            {recipe.denominator && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 sm:col-span-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Denominator</p>
                <p className="text-xs mt-0.5">{recipe.denominator}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Catalog formula</p>
            <p>{metric.formula}</p>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Backend steps</p>
            <ol className="list-decimal pl-5 space-y-2">
              {recipe.steps.map((step, i) => (
                <li key={i} className="leading-relaxed">{step}</li>
              ))}
            </ol>
          </div>

          {metric.gaps && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <span className="font-medium">Gap. </span>{metric.gaps}
            </p>
          )}

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Tables</p>
            <ul className="list-disc pl-5 font-mono text-xs text-gray-600 space-y-0.5">
              {metric.sources.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>

          <pre className="text-[11px] leading-relaxed bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700 whitespace-pre-wrap">
            {RECIPE_BINDINGS}
          </pre>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">SQL to paste</p>
              <CopyButton text={recipe.sql.trim()} label="Copy SQL" />
            </div>
            <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-lg px-3 py-3 overflow-x-auto whitespace-pre">
              {recipe.sql.trim()}
            </pre>
          </div>
        </div>
      </aside>
    </div>
  )
}
